<template>
  <aside
    class="planning-unplanned-panel"
    :class="{
      'planning-unplanned-panel--open': show,
      'planning-unplanned-panel--drop-target': unscheduleDropActive,
      'planning-unplanned-panel--drop-hover': unscheduleDropActive && dropHover,
    }"
  >
    <!-- Alleen op kleine schermen (paneel schuift dan over de kalender) -->
    <div class="planning-unplanned-panel__overlay" @click="$emit('close')"></div>
    <div
      class="planning-unplanned-panel__content"
      @dragover="onPanelDragOver"
      @dragleave="onPanelDragLeave"
      @drop="onPanelDrop"
    >
      <!-- Dropzone: geplande kaart terugslepen = afspraak wissen -->
      <div v-if="unscheduleDropActive" class="planning-unplanned-panel__dropzone">
        <BaseIcon name="calendar-clock" size="28" />
        <strong>Laat los om de afspraak te wissen</strong>
        <span>De werkbon komt terug in “Te plannen”</span>
      </div>
      <div class="planning-unplanned-panel__header">
        <div>
          <h2 class="planning-unplanned-panel__title">
            {{ pickMode ? 'Werkbon kiezen' : 'Te plannen' }}
          </h2>
          <p v-if="pickMode && pickLabel" class="planning-unplanned-panel__pick-label">
            {{ pickLabel }}
          </p>
          <p v-else-if="!pickMode && totalCount > 0" class="planning-unplanned-panel__summary">
            {{ queueSummary }}
          </p>
          <p
            v-if="!pickMode && missingAddressCount > 0"
            class="planning-unplanned-panel__unassigned"
          >
            {{ missingAddressCount }} zonder volledig adres
          </p>
          <p v-if="!pickMode && unassignedCount > 0" class="planning-unplanned-panel__unassigned">
            {{ unassignedCount }} wachten op monteur
          </p>
        </div>
        <button
          type="button"
          class="planning-unplanned-panel__close"
          @click="$emit('close')"
          title="Sluiten"
        >
          <BaseIcon name="xmark" size="20" />
        </button>
      </div>

      <template v-if="!pickMode">
        <div class="planning-unplanned-panel__select-bar">
          <button
            type="button"
            class="planning-unplanned-panel__select-link"
            :disabled="visibleCount === 0"
            @click="selectVisible"
          >
            Alles zichtbaar
          </button>
          <template v-if="filteredTentative.length > 0">
            <span class="planning-unplanned-panel__select-sep">·</span>
            <button
              type="button"
              class="planning-unplanned-panel__select-link"
              @click="selectVisibleTentative"
            >
              Alleen voorlopig
            </button>
          </template>
          <template v-if="selectedCount > 0">
            <span class="planning-unplanned-panel__select-sep">·</span>
            <button
              type="button"
              class="planning-unplanned-panel__select-link"
              @click="$emit('clear-selection')"
            >
              Wissen
            </button>
          </template>
        </div>
      </template>

      <div class="planning-unplanned-panel__search">
        <TextInput
          id="unplanned-search"
          v-model="searchQuery"
          placeholder="Zoek op nummer, klant, adres..."
        >
          <template #prefix>
            <BaseIcon name="search" size="16" />
          </template>
        </TextInput>
      </div>

      <div v-if="showFilters" class="planning-unplanned-panel__filters">
        <template v-if="presentTypes.length > 1">
          <button
            v-for="type in presentTypes"
            :key="type.key"
            type="button"
            class="filter-chip"
            :class="{ 'filter-chip--active': isTypeFilterActive(type.key) }"
            @click="toggleTypeFilter(type.key)"
          >
            {{ type.label }}
          </button>
        </template>
        <button
          v-if="searchMissingAddressCount > 0"
          type="button"
          class="filter-chip"
          :class="{ 'filter-chip--active': onlyMissingAddress }"
          @click="onlyMissingAddress = !onlyMissingAddress"
        >
          Zonder adres ({{ searchMissingAddressCount }})
        </button>
        <button
          v-if="searchWaitingMaterialsCount > 0"
          type="button"
          class="filter-chip"
          :class="{ 'filter-chip--active': onlyWaitingMaterials }"
          @click="onlyWaitingMaterials = !onlyWaitingMaterials"
        >
          Product niet binnen ({{ searchWaitingMaterialsCount }})
        </button>
        <button
          v-if="hasActiveFilters"
          type="button"
          class="filter-chip filter-chip--clear"
          @click="clearFilters"
        >
          Wissen
        </button>
      </div>

      <div class="planning-unplanned-panel__body">
        <div v-for="section in sections" :key="section.key" class="drawer-section">
          <div class="drawer-section__header">
            <h3 class="drawer-section__title">{{ section.title }} ({{ section.count }})</h3>
          </div>
          <p class="drawer-section__hint">{{ section.hint }}</p>

          <div
            v-for="group in section.groups"
            :key="group.key"
            class="unplanned-group"
            :class="{ 'unplanned-group--missing': group.missingAddress }"
          >
            <div class="unplanned-group__header">
              <label v-if="!pickMode" class="unplanned-group__checkbox" @click.stop>
                <input
                  type="checkbox"
                  :checked="isGroupFullySelected(group)"
                  :indeterminate.prop="isGroupPartiallySelected(group)"
                  @change="toggleGroupSelection(section.key, group)"
                />
              </label>
              <button
                type="button"
                class="unplanned-group__toggle"
                @click="toggleExpanded(section.key, group.key)"
              >
                <BaseIcon
                  name="chevron"
                  size="12"
                  class="unplanned-group__chevron"
                  :class="{
                    'unplanned-group__chevron--open': isExpanded(section.key, group.key),
                  }"
                />
                <span class="unplanned-group__title">{{ group.label }}</span>
                <span class="unplanned-group__meta">
                  {{ group.workOrders.length }} · {{ formatDuration(group.totalDuration) }}
                </span>
              </button>
            </div>

            <div v-if="isExpanded(section.key, group.key)" class="unplanned-group__rows">
              <div
                v-for="wo in group.workOrders"
                :key="wo.id"
                class="unplanned-row"
                :class="{
                  'unplanned-row--selected': isSelected(wo.id),
                  'unplanned-row--tentative': section.key === 'tentative' || section.key === 'declined',
                  'unplanned-row--declined': section.key === 'declined',
                }"
                :draggable="!pickMode"
                @click="onRowClick(wo)"
                @dragstart="$emit('dragstart', $event, wo)"
                @dragend="$emit('dragend')"
              >
                <label v-if="!pickMode" class="unplanned-row__checkbox" @click.stop>
                  <input
                    type="checkbox"
                    :checked="isSelected(wo.id)"
                    @change="$emit('toggle-selection', wo.id)"
                  />
                </label>
                <div class="unplanned-row__main">
                  <div class="unplanned-row__top">
                    <span class="unplanned-row__number">{{ wo.work_order_number }}</span>
                    <span v-if="getCustomerName(wo)" class="unplanned-row__customer">
                      {{ getCustomerName(wo) }}
                    </span>
                    <span class="unplanned-row__type">
                      {{ getWorkTypeLabel(wo.work_order_type) }}
                    </span>
                    <span
                      class="unplanned-row__duration"
                      :class="{
                        'unplanned-row__duration--default': !wo.appointment_duration_minutes,
                      }"
                    >
                      {{ formatDuration(wo.appointment_duration_minutes) }}
                    </span>
                    <span
                      v-if="!hasAddress(wo)"
                      class="unplanned-row__flag"
                      title="Geen volledig adres"
                    >
                      <BaseIcon name="info" size="12" />
                    </span>
                    <span
                      v-else-if="getMaterialsState(wo) === 'waiting'"
                      class="unplanned-row__flag"
                      :title="getMaterialsTitle(wo)"
                    >
                      <BaseIcon name="clock" size="12" />
                    </span>
                    <span
                      v-if="isUnassigned(wo)"
                      class="unplanned-row__flag"
                      title="Geen monteur toegewezen"
                    >
                      <BaseIcon name="user" size="12" />
                    </span>
                  </div>
                  <p v-if="getRowSubtitle(wo, section.key)" class="unplanned-row__sub">
                    {{ getRowSubtitle(wo, section.key) }}
                  </p>
                  <p
                    v-if="getWindowChip(wo)"
                    class="unplanned-row__window"
                    :class="`unplanned-row__window--${getWindowChip(wo).tone}`"
                    :title="getWindowChip(wo).title"
                  >
                    <BaseIcon name="calendar-clock" size="11" />
                    <span>{{ getWindowChip(wo).label }}</span>
                    <span v-if="getWindowChip(wo).detail" class="unplanned-row__window-detail">
                      · {{ getWindowChip(wo).detail }}
                    </span>
                  </p>
                  <p
                    v-if="getCustomerResponseChip(wo)"
                    class="unplanned-row__customer"
                    :class="`unplanned-row__customer--${getCustomerResponseChip(wo).tone}`"
                    :title="getCustomerResponseChip(wo).title"
                  >
                    <BaseIcon :name="getCustomerResponseChip(wo).icon" size="11" />
                    <span>{{ getCustomerResponseChip(wo).label }}</span>
                    <span
                      v-if="getCustomerResponseChip(wo).detail"
                      class="unplanned-row__customer-detail"
                    >
                      · {{ getCustomerResponseChip(wo).detail }}
                    </span>
                  </p>
                  <p
                    v-if="getMaterialsSummary(wo)"
                    class="unplanned-row__materials"
                    :class="`unplanned-row__materials--${getMaterialsSummary(wo).state}`"
                  >
                    <BaseIcon name="box" size="11" />
                    <span>{{ getMaterialsSummary(wo).label }}</span>
                    <span
                      v-if="getMaterialsSummary(wo).detail"
                      class="unplanned-row__materials-detail"
                    >
                      · {{ getMaterialsSummary(wo).detail }}
                    </span>
                  </p>
                </div>
                <button
                  v-if="!pickMode"
                  type="button"
                  class="unplanned-row__detail"
                  title="Details"
                  @click.stop="$emit('select', wo)"
                >
                  <BaseIcon name="information-circle" size="16" />
                </button>
                <Btn
                  v-else
                  size="sm"
                  variant="secondary"
                  icon="plus"
                  text="Kiezen"
                  @click.stop="$emit('pick', wo)"
                />
              </div>
            </div>
          </div>
        </div>

        <div v-if="sections.length === 0" class="empty-unplanned">
          <BaseIcon :name="searchQuery || hasActiveFilters ? 'search' : 'check-circle'" size="24" />
          <p>
            {{
              searchQuery || hasActiveFilters
                ? 'Geen resultaten gevonden'
                : 'Geen open werkbonnen voor planning'
            }}
          </p>
          <button
            v-if="hasActiveFilters || searchQuery"
            type="button"
            class="planning-unplanned-panel__select-link"
            @click="clearFiltersAndSearch"
          >
            Filters wissen
          </button>
        </div>
      </div>

      <PlanningBulkActionPanel
        v-if="!pickMode"
        :selected-count="selectedCount"
        :selection-summary="selectionSummary"
        :loading="bulkLoading"
        :active-action="bulkActiveAction"
        @bulk-confirm="$emit('bulk-confirm')"
        @bulk-delete="$emit('bulk-delete')"
        @plan="$emit('open-bulk-plan')"
      />
    </div>
  </aside>
</template>

<script>
  import { computed, ref, watch } from 'vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import TextInput from '@/components/inputs/TextInput.vue';
  import Btn from '@/components/partials/Btn.vue';
  import PlanningBulkActionPanel from '@/components/planning/PlanningBulkActionPanel.vue';
  import { PLANNING_WORK_TYPE_OPTIONS, getWorkOrderTypeLabel } from '@/constants/planningConstants';
  import { getAppointmentStart, getMaterialsState, getMaterialsSummary } from '@/utils/workOrders';
  import {
    getWorkOrderWindow,
    formatPlanningWindow,
    evaluatePlanningWindow,
  } from '@/utils/planningWindow';
  import {
    formatPlanningDuration,
    getCustomerName,
    getStreetLabel,
    groupWorkOrdersByCity,
    hasCompleteAddress,
  } from '@/utils/planningGroups';

  export default {
    name: 'PlanningUnplannedPanel',
    components: { BaseIcon, TextInput, Btn, PlanningBulkActionPanel },
    props: {
      show: { type: Boolean, default: false },
      unplannedWorkOrders: { type: Array, default: () => [] },
      tentativeWorkOrders: { type: Array, default: () => [] },
      getStatusLabel: { type: Function, required: true },
      formatDateTime: { type: Function, required: true },
      pickMode: { type: Boolean, default: false },
      pickLabel: { type: String, default: '' },
      // YYYY-MM-DD of the slot being filled in pick mode; colours the window chip.
      pickDate: { type: String, default: '' },
      selectedIds: { type: Object, default: () => new Set() },
      selectedCount: { type: Number, default: 0 },
      selectionSummary: {
        type: Object,
        default: () => ({ totalDuration: 0, tentativeCount: 0 }),
      },
      bulkLoading: { type: Boolean, default: false },
      bulkActiveAction: { type: String, default: null },
      // True while a *planned* card is being dragged: the panel becomes a
      // drop target that clears the appointment.
      unscheduleDropActive: { type: Boolean, default: false },
    },
    emits: [
      'close',
      'schedule',
      'select',
      'pick',
      'delete',
      'dragstart',
      'dragend',
      'unschedule',
      'toggle-selection',
      'select-visible',
      'select-ids',
      'deselect-ids',
      'clear-selection',
      'bulk-confirm',
      'bulk-delete',
      'open-bulk-plan',
    ],
    setup(props, { emit }) {
      const searchQuery = ref('');
      const typeFilter = ref(new Set());
      const onlyMissingAddress = ref(false);
      const onlyWaitingMaterials = ref(false);
      const expandedKeys = ref(new Set());

      // --- Dropzone (geplande kaart → paneel = afspraak wissen) ---
      const dropHover = ref(false);

      const onPanelDragOver = (e) => {
        if (!props.unscheduleDropActive) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        dropHover.value = true;
      };

      const onPanelDragLeave = (e) => {
        if (e.relatedTarget && e.currentTarget.contains(e.relatedTarget)) return;
        dropHover.value = false;
      };

      const onPanelDrop = (e) => {
        dropHover.value = false;
        if (!props.unscheduleDropActive) return;
        e.preventDefault();
        const workOrderId = e.dataTransfer.getData('workOrderId');
        if (workOrderId) emit('unschedule', workOrderId);
      };

      watch(
        () => props.unscheduleDropActive,
        (active) => {
          if (!active) dropHover.value = false;
        }
      );

      const resetDrawerState = () => {
        searchQuery.value = '';
        typeFilter.value = new Set();
        onlyMissingAddress.value = false;
        onlyWaitingMaterials.value = false;
        expandedKeys.value = new Set();
      };

      watch(
        () => props.show,
        (open) => {
          if (!open) resetDrawerState();
        }
      );

      const matchesSearch = (wo) => {
        if (!searchQuery.value) return true;
        const q = searchQuery.value.toLowerCase();
        const fields = [
          wo.work_order_number,
          wo.title,
          wo.project?.lead?.name,
          wo.project?.title,
          wo.installation_address?.street,
          wo.installation_address?.city,
          wo.installation_address?.postal_code,
          wo.project?.installation_address?.street,
          wo.project?.installation_address?.city,
          wo.items?.[0]?.description,
        ];
        return fields.some((f) => f && String(f).toLowerCase().includes(q));
      };

      const isUnassigned = (wo) => {
        const primary = wo.assigned_to?.id || wo.assigned_to;
        if (primary) return false;
        const helperCount = (wo.assigned_user_ids?.length || 0) + (wo.assigned_users?.length || 0);
        return helperCount === 0;
      };

      const hasAddress = (wo) => hasCompleteAddress(wo);

      const matchesFilters = (wo) => {
        if (typeFilter.value.size > 0) {
          const type = wo.work_order_type || 'installation';
          if (!typeFilter.value.has(type)) return false;
        }
        if (onlyMissingAddress.value && hasAddress(wo)) return false;
        if (onlyWaitingMaterials.value && getMaterialsState(wo) !== 'waiting') return false;
        return true;
      };

      const searchedUnplanned = computed(() => props.unplannedWorkOrders.filter(matchesSearch));
      const searchedTentative = computed(() => props.tentativeWorkOrders.filter(matchesSearch));

      const filteredUnplanned = computed(() => searchedUnplanned.value.filter(matchesFilters));
      const filteredTentative = computed(() => searchedTentative.value.filter(matchesFilters));

      const searchedAll = computed(() => [...searchedUnplanned.value, ...searchedTentative.value]);

      const presentTypes = computed(() => {
        const present = new Set(
          [...props.unplannedWorkOrders, ...props.tentativeWorkOrders].map(
            (wo) => wo.work_order_type || 'installation'
          )
        );
        return PLANNING_WORK_TYPE_OPTIONS.filter((t) => present.has(t.key));
      });

      const searchMissingAddressCount = computed(
        () => searchedAll.value.filter((wo) => !hasAddress(wo)).length
      );
      const searchWaitingMaterialsCount = computed(
        () => searchedAll.value.filter((wo) => getMaterialsState(wo) === 'waiting').length
      );

      const showFilters = computed(
        () =>
          presentTypes.value.length > 1 ||
          searchMissingAddressCount.value > 0 ||
          searchWaitingMaterialsCount.value > 0
      );

      const hasActiveFilters = computed(
        () => typeFilter.value.size > 0 || onlyMissingAddress.value || onlyWaitingMaterials.value
      );

      const sections = computed(() => {
        const result = [];
        // Customer said "past niet" on the tentative appointment: top of the
        // call list, nothing is changed automatically.
        const declined = filteredTentative.value.filter(
          (wo) => wo.customer_confirmation?.response === 'declined'
        );
        const otherTentative = filteredTentative.value.filter(
          (wo) => wo.customer_confirmation?.response !== 'declined'
        );
        if (declined.length > 0) {
          result.push({
            key: 'declined',
            title: 'Past niet bij klant',
            hint: 'Klant heeft de voorlopige afspraak afgewezen — bel om een nieuw moment af te spreken',
            count: declined.length,
            groups: groupWorkOrdersByCity(declined),
          });
        }
        if (otherTentative.length > 0) {
          result.push({
            key: 'tentative',
            title: 'Voorlopig',
            hint: 'Nog niet bevestigd met klant',
            count: otherTentative.length,
            groups: groupWorkOrdersByCity(otherTentative),
          });
        }
        if (filteredUnplanned.value.length > 0) {
          result.push({
            key: 'unplanned',
            title: 'Zonder datum',
            hint: 'Vink een plaats aan of sleep naar de kalender',
            count: filteredUnplanned.value.length,
            groups: groupWorkOrdersByCity(filteredUnplanned.value),
          });
        }
        return result;
      });

      const visibleWorkOrders = computed(() => [
        ...filteredTentative.value,
        ...filteredUnplanned.value,
      ]);
      const visibleCount = computed(() => visibleWorkOrders.value.length);

      const totalCount = computed(
        () => props.unplannedWorkOrders.length + props.tentativeWorkOrders.length
      );

      const unassignedCount = computed(() => {
        const all = [...props.unplannedWorkOrders, ...props.tentativeWorkOrders];
        return all.filter(isUnassigned).length;
      });

      const missingAddressCount = computed(() => {
        const all = [...props.unplannedWorkOrders, ...props.tentativeWorkOrders];
        return all.filter((wo) => !hasAddress(wo)).length;
      });

      const queueSummary = computed(() => {
        const open = props.unplannedWorkOrders.length;
        const tentative = props.tentativeWorkOrders.length;
        const parts = [];
        if (open > 0) parts.push(`${open} zonder datum`);
        if (tentative > 0) parts.push(`${tentative} voorlopig`);
        return parts.join(' · ');
      });

      const isSelected = (id) => props.selectedIds.has(id);
      const getWorkTypeLabel = (type) => getWorkOrderTypeLabel(type);
      const formatDuration = (minutes) => formatPlanningDuration(minutes);

      const getRowSubtitle = (wo, sectionKey) => {
        if (sectionKey === 'tentative' || sectionKey === 'declined') {
          const start = getAppointmentStart(wo);
          if (start) return props.formatDateTime(start);
        }
        return getStreetLabel(wo);
      };

      const getMaterialsTitle = (wo) => {
        const summary = getMaterialsSummary(wo);
        if (!summary) return 'Product nog niet binnen';
        return summary.detail ? `${summary.label} · ${summary.detail}` : summary.label;
      };

      // Customer planning window chip (inherited from the project). In pick
      // mode the chip is coloured by whether the pending slot date fits.
      const getWindowChip = (wo) => {
        const window = getWorkOrderWindow(wo);
        const label = formatPlanningWindow(window);
        if (!label && !window?.note) return null;

        let tone =
          window?.mode === 'only' ? 'hard' : window?.mode === 'prefer' ? 'soft' : 'neutral';
        let detail = null;
        if (props.pickMode && props.pickDate && label) {
          const verdict = evaluatePlanningWindow(window, props.pickDate);
          if (verdict.match === 'inside') {
            tone = 'ok';
            detail = 'past';
          } else if (verdict.match === 'outside') {
            tone = verdict.hard ? 'blocked' : 'warn';
            detail = verdict.hard ? 'past niet' : 'eerst bellen';
          }
        }

        const fromCustomer = window?.source === 'customer';
        const baseTitle = window?.note ? `${label ? `${label} — ` : ''}${window.note}` : label;
        return {
          label: label || 'Notitie klant',
          detail: detail || (fromCustomer ? 'van klant' : null),
          tone,
          title: fromCustomer ? `${baseTitle} (doorgegeven door klant)` : baseTitle,
        };
      };

      // Tentative-appointment handshake state ("klopt deze afspraak?").
      const getCustomerResponseChip = (wo) => {
        const c = wo.customer_confirmation;
        if (!c || !getAppointmentStart(wo)) return null;
        if (c.response === 'declined') {
          return {
            tone: 'declined',
            icon: 'phone',
            label: 'Klant: past niet',
            detail: c.reason ? c.reason : 'bel voor nieuw moment',
            title: c.reason
              ? `Klant heeft afgewezen: ${c.reason}`
              : 'Klant heeft de voorlopige afspraak afgewezen',
          };
        }
        if (c.response === 'accepted') {
          return {
            tone: 'accepted',
            icon: 'check-circle',
            label: 'Klant akkoord',
            detail: null,
            title: 'Klant heeft de afspraak bevestigd',
          };
        }
        if (c.requested_at) {
          return {
            tone: 'pending',
            icon: 'clock',
            label: 'Wacht op klant',
            detail: null,
            title: `Bevestigingsverzoek verstuurd op ${new Date(c.requested_at).toLocaleString('nl-NL', { dateStyle: 'medium', timeStyle: 'short' })}`,
          };
        }
        return null;
      };

      const expandKey = (sectionKey, groupKey) => `${sectionKey}:${groupKey}`;

      const isExpanded = (sectionKey, groupKey) => {
        if (searchQuery.value) return true;
        const section = sections.value.find((item) => item.key === sectionKey);
        if (section && section.groups.length === 1) return true;
        return expandedKeys.value.has(expandKey(sectionKey, groupKey));
      };

      const toggleExpanded = (sectionKey, groupKey) => {
        const key = expandKey(sectionKey, groupKey);
        const next = new Set(expandedKeys.value);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        expandedKeys.value = next;
      };

      const expandGroup = (sectionKey, groupKey) => {
        const key = expandKey(sectionKey, groupKey);
        if (expandedKeys.value.has(key)) return;
        const next = new Set(expandedKeys.value);
        next.add(key);
        expandedKeys.value = next;
      };

      const isGroupFullySelected = (group) =>
        group.workOrders.length > 0 && group.workOrders.every((wo) => isSelected(wo.id));

      const isGroupPartiallySelected = (group) => {
        const selected = group.workOrders.filter((wo) => isSelected(wo.id)).length;
        return selected > 0 && selected < group.workOrders.length;
      };

      const toggleGroupSelection = (sectionKey, group) => {
        const ids = group.workOrders.map((wo) => wo.id);
        if (isGroupFullySelected(group)) {
          emit('deselect-ids', ids);
          return;
        }
        emit('select-ids', ids);
        expandGroup(sectionKey, group.key);
      };

      const selectVisible = () => {
        emit(
          'select-visible',
          visibleWorkOrders.value.map((wo) => wo.id)
        );
      };

      const selectVisibleTentative = () => {
        emit(
          'select-visible',
          filteredTentative.value.map((wo) => wo.id)
        );
      };

      const isTypeFilterActive = (key) => typeFilter.value.has(key);

      const toggleTypeFilter = (key) => {
        const next = new Set(typeFilter.value);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        typeFilter.value = next;
      };

      const clearFilters = () => {
        typeFilter.value = new Set();
        onlyMissingAddress.value = false;
        onlyWaitingMaterials.value = false;
      };

      const clearFiltersAndSearch = () => {
        clearFilters();
        searchQuery.value = '';
      };

      const onRowClick = (wo) => {
        if (props.pickMode) {
          emit('pick', wo);
          return;
        }
        emit('toggle-selection', wo.id);
      };

      return {
        searchQuery,
        onlyMissingAddress,
        onlyWaitingMaterials,
        presentTypes,
        showFilters,
        hasActiveFilters,
        searchMissingAddressCount,
        searchWaitingMaterialsCount,
        filteredTentative,
        sections,
        visibleCount,
        totalCount,
        unassignedCount,
        missingAddressCount,
        queueSummary,
        isUnassigned,
        isSelected,
        getCustomerName,
        getWorkTypeLabel,
        hasAddress,
        formatDuration,
        getRowSubtitle,
        getMaterialsSummary,
        getMaterialsTitle,
        getWindowChip,
        getCustomerResponseChip,
        dropHover,
        onPanelDragOver,
        onPanelDragLeave,
        onPanelDrop,
        isExpanded,
        toggleExpanded,
        isGroupFullySelected,
        isGroupPartiallySelected,
        toggleGroupSelection,
        selectVisible,
        selectVisibleTentative,
        isTypeFilterActive,
        toggleTypeFilter,
        clearFilters,
        clearFiltersAndSearch,
        onRowClick,
        getMaterialsState,
      };
    },
  };
</script>

<style scoped lang="scss">
  // Desktop: gedockt paneel naast de kalender (geen backdrop, zodat drag &
  // drop van kaart naar kalendercel gewoon werkt). Kleine schermen: schuift
  // als drawer over de kalender heen — daar sleep je toch niet.
  .planning-unplanned-panel {
    --panel-width: 380px;

    position: relative;
    flex: 0 0 var(--panel-width);
    width: var(--panel-width);
    min-width: 0;
    display: none;
    align-self: stretch;

    &--open {
      display: flex;
    }

    &__overlay {
      display: none;
    }

    &__content {
      position: relative;
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
      background: white;
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;
      overflow: hidden;
      transition:
        border-color 0.15s,
        box-shadow 0.15s;
    }

    &--drop-target &__content {
      border-color: var(--brand-300);
      border-style: dashed;
    }

    &--drop-hover &__content {
      border-color: var(--brand-500);
      box-shadow: 0 0 0 3px var(--brand-100);
    }

    &__dropzone {
      position: absolute;
      inset: 0;
      z-index: 5;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 0.375rem;
      padding: 1.5rem;
      text-align: center;
      background: rgba(255, 255, 255, 0.92);
      color: var(--brand-700);
      // Dragover/drop moeten op __content landen, niet op deze laag
      pointer-events: none;

      strong {
        font-size: 0.9375rem;
      }
      span {
        font-size: 0.8125rem;
        color: var(--slate-600);
      }
    }

    @include lg-down {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 1000;
      width: auto;
      flex: none;
      display: block;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.2s ease;

      &--open {
        display: block;
        pointer-events: all;
        opacity: 1;
      }

      &__overlay {
        display: block;
        position: absolute;
        inset: 0;
        background: rgba(0, 0, 0, 0.5);
        cursor: pointer;
      }

      &__content {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        width: 90vw;
        max-width: 460px;
        border: 0;
        border-radius: 0;
        box-shadow: -4px 0 24px rgba(0, 0, 0, 0.15);
        transform: translateX(100%);
        transition: transform 0.2s ease;
      }

      &--open &__content {
        transform: translateX(0);
      }
    }

    &__header {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 1rem 1.25rem;
      border-bottom: 1px solid var(--slate-200);
    }

    &__title {
      margin: 0;
      font-size: 1.125rem;
      font-weight: 600;
      color: var(--slate-900);
    }

    &__pick-label {
      margin: 0.25rem 0 0;
      font-size: 0.8125rem;
      font-weight: 500;
      color: var(--brand-600);
    }

    &__summary {
      margin: 0.25rem 0 0;
      font-size: 0.8125rem;
      color: var(--slate-600);
    }

    &__unassigned {
      margin: 0.25rem 0 0;
      font-size: 0.75rem;
      font-weight: 500;
      color: var(--amber-700);
    }

    &__close {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      border: none;
      border-radius: 0.375rem;
      background: transparent;
      color: var(--slate-600);
      cursor: pointer;
      &:hover {
        background: var(--slate-100);
        color: var(--slate-900);
      }
    }

    &__select-bar {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      gap: 0.375rem;
      padding: 0.75rem 1rem 0;
      font-size: 0.75rem;
    }

    &__select-link {
      border: none;
      background: none;
      padding: 0;
      font-size: 0.75rem;
      font-weight: 500;
      color: var(--brand-600);
      cursor: pointer;

      &:hover:not(:disabled) {
        color: var(--brand-700);
      }

      &:disabled {
        color: var(--slate-400);
        cursor: default;
      }
    }

    &__select-sep {
      color: var(--slate-300);
    }

    &__search {
      flex-shrink: 0;
      padding: 0.75rem 1rem 0;
    }

    &__filters {
      flex-shrink: 0;
      display: flex;
      flex-wrap: wrap;
      gap: 0.375rem;
      padding: 0.75rem 1rem 0;
    }

    &__body {
      flex: 1;
      overflow-y: auto;
      padding: 1rem;
    }
  }

  .filter-chip {
    border: 1px solid var(--slate-200);
    background: white;
    color: var(--slate-700);
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1.2;
    padding: 0.25rem 0.625rem;
    border-radius: 9999px;
    cursor: pointer;

    &:hover {
      border-color: var(--slate-300);
      background: var(--slate-50);
    }

    &--active {
      border-color: var(--brand-300);
      background: var(--brand-50);
      color: var(--brand-700);
    }

    &--clear {
      border-color: transparent;
      color: var(--brand-600);

      &:hover {
        background: var(--brand-50);
      }
    }
  }

  .drawer-section {
    margin-bottom: 1.25rem;

    &__header {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 0.25rem;
    }

    &__title {
      margin: 0;
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--slate-700);
    }

    &__hint {
      margin: 0 0 0.5rem 0;
      font-size: 0.75rem;
      color: var(--slate-500);
    }
  }

  .unplanned-group {
    border: 1px solid var(--slate-200);
    border-radius: 0.5rem;
    margin-bottom: 0.5rem;
    background: white;
    overflow: hidden;

    &--missing {
      border-color: var(--amber-200);
    }

    &__header {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      padding: 0.5rem 0.625rem;
      background: var(--slate-50);

      &:hover {
        background: var(--slate-100);
      }
    }

    &--missing &__header {
      background: var(--amber-50);

      &:hover {
        background: var(--amber-100);
      }
    }

    &__checkbox {
      flex-shrink: 0;
      display: inline-flex;
      cursor: pointer;

      input {
        width: 1rem;
        height: 1rem;
        cursor: pointer;
      }
    }

    &__toggle {
      flex: 1;
      min-width: 0;
      display: flex;
      align-items: center;
      gap: 0.375rem;
      border: none;
      background: none;
      padding: 0;
      cursor: pointer;
      text-align: left;
    }

    &__chevron {
      flex-shrink: 0;
      color: var(--slate-500);
      transform: rotate(90deg);
      transition: transform 0.15s ease;

      &--open {
        transform: rotate(180deg);
      }
    }

    &__title {
      font-size: 0.8125rem;
      font-weight: 600;
      color: var(--slate-800);
      @include ellipsis(1);
    }

    &__meta {
      margin-left: auto;
      flex-shrink: 0;
      font-size: 0.75rem;
      font-weight: 500;
      color: var(--slate-500);
    }

    &__rows {
      border-top: 1px solid var(--slate-200);
    }
  }

  .unplanned-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.625rem;
    cursor: pointer;
    border-bottom: 1px solid var(--slate-100);

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: var(--slate-50);
    }

    &--selected {
      background: var(--brand-50);

      &:hover {
        background: var(--brand-50);
      }
    }

    &--tentative {
      background: var(--amber-50);

      &:hover {
        background: var(--amber-50);
      }

      &.unplanned-row--selected {
        background: var(--brand-50);
      }
    }

    &--declined {
      background: var(--red-50);
      box-shadow: inset 3px 0 0 var(--red-400);

      &:hover {
        background: var(--red-50);
      }
    }

    &__checkbox {
      flex-shrink: 0;
      display: inline-flex;
      cursor: pointer;

      input {
        width: 1rem;
        height: 1rem;
        cursor: pointer;
      }
    }

    &__main {
      flex: 1;
      min-width: 0;
    }

    &__top {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      min-width: 0;
    }

    &__number {
      font-weight: 600;
      font-size: 0.8125rem;
      color: var(--slate-900);
      flex-shrink: 0;
    }

    &__customer {
      min-width: 0;
      flex: 1;
      font-size: 0.8125rem;
      font-weight: 500;
      color: var(--slate-800);
      @include ellipsis(1);
    }

    &__type {
      margin-left: auto;
      font-size: 0.6875rem;
      font-weight: 500;
      color: var(--slate-500);
      flex-shrink: 0;
    }

    &__duration {
      font-size: 0.75rem;
      font-weight: 500;
      color: var(--slate-500);
      flex-shrink: 0;

      &--default {
        color: var(--amber-700);
      }
    }

    &__flag {
      display: inline-flex;
      color: var(--amber-700);
      flex-shrink: 0;
    }

    &__sub {
      margin: 0.125rem 0 0;
      font-size: 0.75rem;
      color: var(--slate-500);
      @include ellipsis(1);
    }

    &__materials {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      margin: 0.125rem 0 0;
      font-size: 0.6875rem;
      font-weight: 500;
      color: var(--amber-700);
      @include ellipsis(1);

      &--in {
        color: var(--green-700);
      }
    }

    &__materials-detail {
      font-weight: 400;
      color: var(--slate-500);
    }

    &__window {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      margin: 0.125rem 0 0;
      font-size: 0.6875rem;
      font-weight: 500;
      color: var(--slate-600);
      @include ellipsis(1);

      &--hard {
        color: var(--red-700);
      }
      &--soft {
        color: var(--amber-700);
      }
      &--ok {
        color: var(--green-700);
      }
      &--warn {
        color: var(--amber-700);
      }
      &--blocked {
        color: var(--red-700);
        text-decoration: line-through;
      }
    }

    &__window-detail {
      font-weight: 600;
      text-decoration: none;
    }

    &__customer {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      margin: 0.125rem 0 0;
      font-size: 0.6875rem;
      font-weight: 600;
      @include ellipsis(1);

      &--declined {
        color: var(--red-700);
      }
      &--accepted {
        color: var(--green-700);
      }
      &--pending {
        color: var(--slate-500);
        font-weight: 500;
      }
    }

    &__customer-detail {
      font-weight: 500;
    }

    &__detail {
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border: none;
      border-radius: 0.375rem;
      background: transparent;
      color: var(--slate-400);
      cursor: pointer;

      &:hover {
        background: var(--slate-100);
        color: var(--slate-700);
      }
    }
  }

  .empty-unplanned {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    padding: 2rem;
    color: var(--green-600);
    font-size: 0.875rem;
    text-align: center;
  }
</style>
